import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ResetPasswordSchema } from "schema"
import { z } from "zod"

type FieldValues = z.infer<typeof ResetPasswordSchema>

type Props = {
  onSubmit: SubmitHandler<FieldValues>
  isPending: boolean
  mode: "activateAccount" | "default"
  token: string
}

export function ResetPasswordForm({ onSubmit, isPending, mode, token }: Props) {
  const { t } = useTranslation()
  const form = useForm<FieldValues>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
      token,
    },
  })

  return (
    <Form
      {...form}
      errorTranslations={getErrorTranslations()}
      onSubmit={form.handleSubmit(onSubmit)}
      className="w-full"
    >
      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("auth.password.field")}</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder={t("auth.password.placeholder")}
                className="bg-background/75"
                {...field}
                onBlur={() => form.trigger("password")}
              />
            </FormControl>
            <FormDescription />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="confirmPassword"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("auth.confirmPassword.field")}</FormLabel>
            <FormControl>
              <Input
                {...field}
                type="password"
                placeholder={t("auth.confirmPassword.placeholder")}
                onChange={(e) => {
                  field.onChange(e)
                  form.setValue("confirmPassword", e.target.value, { shouldValidate: true })
                }}
                className="bg-background/75"
              />
            </FormControl>
            <FormDescription />
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex flex-col gap-4 pt-4">
        <Button disabled={isPending || !form.formState.isValid}>
          {t(`auth.resetPassword.submit.${mode}`)}
        </Button>
      </div>
    </Form>
  )
}
